import type { PanOh } from '../web-components/pan-oh/PanOh';

export type EasingFunction =
    | 'linear'
    | 'ease-in-out'
    | 'ease-in'
    | 'ease-out'
    | 'ease-in-back'
    | 'ease-out-back'
    | 'ease-in-out-back'
    | 'ease-in-elastic'
    | 'ease-out-elastic'
    | 'ease-out-bounce';

export function animatePanorama(
    pano: PanOh,
    targetYaw: number,
    targetPitch: number,
    targetZoom: number,
    duration = 1000,
    easing: EasingFunction = 'ease-in-out'
): Promise<void> {
    const startYaw = pano.yaw;
    const startPitch = pano.pitch;
    const startZoom = pano.zoom;

    return new Promise((resolve) => {
        const startTime = performance.now();

        const animate = (currentTime: number) => {
            const elapsed = currentTime - startTime;
            let progress = Math.min(elapsed / duration, 1);

            let eased: number;
            switch (easing) {
                case 'linear':
                    eased = progress;
                    break;

                case 'ease-in-out':
                    eased =
                        progress < 0.5
                            ? 4 * progress * progress * progress
                            : 1 - Math.pow(-2 * progress + 2, 3) / 2;
                    break;

                case 'ease-in':
                    eased = progress * progress * progress;
                    break;

                case 'ease-out':
                    eased = 1 - Math.pow(1 - progress, 3);
                    break;

                case 'ease-in-back': {
                    const c1 = 1.70158;
                    const c3 = c1 + 1;
                    eased =
                        c3 * progress * progress * progress -
                        c1 * progress * progress;
                    break;
                }

                case 'ease-out-back': {
                    const c1b = 1.70158;
                    const c3b = c1b + 1;
                    eased =
                        1 +
                        c3b * Math.pow(progress - 1, 3) +
                        c1b * Math.pow(progress - 1, 2);
                    break;
                }

                case 'ease-in-out-back': {
                    const c1c = 1.70158;
                    const c2c = c1c * 1.525;
                    eased =
                        progress < 0.5
                            ? (Math.pow(2 * progress, 2) *
                                  ((c2c + 1) * 2 * progress - c2c)) /
                              2
                            : (Math.pow(2 * progress - 2, 2) *
                                  ((c2c + 1) * (progress * 2 - 2) + c2c) +
                                  2) /
                              2;
                    break;
                }

                case 'ease-in-elastic': {
                    const c4 = (2 * Math.PI) / 3;
                    eased =
                        progress === 0
                            ? 0
                            : progress === 1
                            ? 1
                            : -Math.pow(2, 10 * progress - 10) *
                              Math.sin((progress * 10 - 10.75) * c4);
                    break;
                }

                case 'ease-out-elastic': {
                    const c4b = (2 * Math.PI) / 3;
                    eased =
                        progress === 0
                            ? 0
                            : progress === 1
                            ? 1
                            : Math.pow(2, -10 * progress) *
                                  Math.sin((progress * 10 - 0.75) * c4b) +
                              1;
                    break;
                }

                case 'ease-out-bounce': {
                    const n1 = 7.5625;
                    const d1 = 2.75;
                    if (progress < 1 / d1) {
                        eased = n1 * progress * progress;
                    } else if (progress < 2 / d1) {
                        eased = n1 * (progress -= 1.5 / d1) * progress + 0.75;
                    } else if (progress < 2.5 / d1) {
                        eased =
                            n1 * (progress -= 2.25 / d1) * progress + 0.9375;
                    } else {
                        eased =
                            n1 * (progress -= 2.625 / d1) * progress + 0.984375;
                    }
                    break;
                }

                default:
                    eased = progress;
                    break;
            }

            pano.yaw = startYaw + (targetYaw - startYaw) * eased;
            pano.pitch = startPitch + (targetPitch - startPitch) * eased;
            pano.zoom = startZoom + (targetZoom - startZoom) * eased;

            if (progress < 1) {
                requestAnimationFrame(animate);
            } else {
                resolve();
            }
        };

        requestAnimationFrame(animate);
    });
}
